import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './ConsentForm.css';

const API_URL = process.env.REACT_APP_API_URL;

// Country codes list with ISO country codes for flag images
const countryCodes = [
  { code: '+90', country: 'Türkiye', iso: 'tr' },
  { code: '+974', country: 'Qatar', iso: 'qa' },
  { code: '+971', country: 'UAE', iso: 'ae' },
  { code: '+966', country: 'Saudi Arabia', iso: 'sa' },
  { code: '+970', country: 'Palestine', iso: 'ps' },
  { code: '+973', country: 'Bahrain', iso: 'bh' },
  { code: '+965', country: 'Kuwait', iso: 'kw' },
  { code: '+968', country: 'Oman', iso: 'om' },
  { code: '+962', country: 'Jordan', iso: 'jo' },
  { code: '+961', country: 'Lebanon', iso: 'lb' },
  { code: '+20', country: 'Egypt', iso: 'eg' },
  { code: '+212', country: 'Morocco', iso: 'ma' },
  { code: '+1', country: 'USA/Canada', iso: 'us' },
  { code: '+44', country: 'UK', iso: 'gb' },
  { code: '+49', country: 'Germany', iso: 'de' },
  { code: '+33', country: 'France', iso: 'fr' },
  { code: '+39', country: 'Italy', iso: 'it' },
  { code: '+34', country: 'Spain', iso: 'es' },
  { code: '+31', country: 'Netherlands', iso: 'nl' },
  { code: '+91', country: 'India', iso: 'in' },
  { code: '+92', country: 'Pakistan', iso: 'pk' },
  { code: '+86', country: 'China', iso: 'cn' },
  { code: '+81', country: 'Japan', iso: 'jp' },
  { code: '+82', country: 'South Korea', iso: 'kr' },
  { code: '+65', country: 'Singapore', iso: 'sg' },
  { code: '+60', country: 'Malaysia', iso: 'my' },
  { code: '+62', country: 'Indonesia', iso: 'id' },
  { code: '+63', country: 'Philippines', iso: 'ph' },
  { code: '+7', country: 'Russia', iso: 'ru' },
  { code: '+55', country: 'Brazil', iso: 'br' },
  { code: '+52', country: 'Mexico', iso: 'mx' },
  { code: '+27', country: 'South Africa', iso: 'za' },
  { code: '+234', country: 'Nigeria', iso: 'ng' },
  { code: '+61', country: 'Australia', iso: 'au' },
  { code: '+64', country: 'New Zealand', iso: 'nz' },
];

// Helper function to get flag image URL
const getFlagUrl = (iso) => `https://flagcdn.com/w40/${iso}.png`;

const ConsentForm = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFullTerms, setShowFullTerms] = useState(false);

  const dropdownRef = useRef(null);
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  const handleCancel = () => {
    logout();
    navigate('/');
  };

  // Enable page scrolling for this page
  useEffect(() => {
    // Store original styles
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalRootOverflow = document.getElementById('root')?.style.overflow;
    
    // Enable scrolling
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    if (document.getElementById('root')) {
      document.getElementById('root').style.overflow = 'auto';
      document.getElementById('root').style.height = 'auto';
    }
    
    // Restore on unmount
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      if (document.getElementById('root')) {
        document.getElementById('root').style.overflow = originalRootOverflow;
        document.getElementById('root').style.height = '100%';
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      setError('Please accept the terms and conditions');
      return;
    }

    // Validate name fields
    if (!firstName.trim()) {
      setError('Please enter your first name');
      return;
    }
    if (!lastName.trim()) {
      setError('Please enter your last name');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate phone
    if (!phone || phone.length < 7) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Session expired. Please go back and enter your access code again.');
        return;
      }

      const fullPhone = `${selectedCountry.code}${phone}`;
      const response = await axios.post(
        `${API_URL}/api/auth/register-guest`,
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: fullPhone,
          termsAccepted,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      login(response.data.token, response.data.user);
      navigate('/menu');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to submit registration';
      setError(errorMessage);

      // If session expired, redirect to home after showing error
      if (err.response?.status === 401) {
        setTimeout(() => {
          logout();
          navigate('/');
        }, 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setDropdownOpen(false);
  };

  return (
    <div className="consent-container">
      <div className="background" />
      <div className="light-rays">
        <div className="wave wave-top"></div>
        <div className="wave wave-middle"></div>
        <div className="wave wave-bottom"></div>
      </div>

      {/* Floating organ bubbles */}
      <div className="bubble-sphere bubble-eye">
        <div className="sphere-inner">
          <img src="/eye.png" alt="Eye" className="bubble-img" />
        </div>
      </div>
      <div className="bubble-sphere bubble-kidneys">
        <div className="sphere-inner">
          <img src="/kidneyes.png" alt="Kidneys" className="bubble-img" />
        </div>
      </div>
      <div className="bubble-sphere bubble-liver">
        <div className="sphere-inner">
          <img src="/liver.png" alt="Liver" className="bubble-img" />
        </div>
      </div>
      <div className="bubble-sphere bubble-abdomen">
        <div className="sphere-inner">
          <img src="/abdomen.png" alt="Abdomen" className="bubble-img" />
        </div>
      </div>
      <div className="bubble-sphere bubble-lungs">
        <div className="sphere-inner">
          <img src="/lungs.png" alt="Lungs" className="bubble-img" />
        </div>
      </div>

      <div className="consent-card">
        <img src="/DarkLogo.png" alt="METATIP Logo" className="consent-logo" />
        <h2>Registration & Consent</h2>
        <p className="consent-intro">Please provide your details to access the anatomical models.</p>

        <form onSubmit={handleSubmit}>
          <div className="name-row">
            <div className="input-group">
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                required
              />
            </div>
            <div className="input-group">
              <input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                required
              />
            </div>
          </div>
          <div className="input-group">
            <input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="phone-input-wrapper">
            <div 
              className={`country-select ${dropdownOpen ? 'open' : ''}`}
              ref={dropdownRef}
            >
              <button
                type="button"
                className="country-select-btn"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <img 
                  src={getFlagUrl(selectedCountry.iso)} 
                  alt={selectedCountry.country} 
                  className="flag"
                />
                <span className="code">{selectedCountry.code}</span>
                <span className="arrow">▼</span>
              </button>
              <div className="country-dropdown">
                {countryCodes.map((country) => (
                  <div
                    key={country.code}
                    className={`country-option ${selectedCountry.code === country.code ? 'selected' : ''}`}
                    onClick={() => handleCountrySelect(country)}
                  >
                    <img 
                      src={getFlagUrl(country.iso)} 
                      alt={country.country} 
                      className="flag"
                    />
                    <span className="name">{country.country}</span>
                    <span className="dial-code">{country.code}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="input-group">
              <input
                type="tel"
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ''))}
                required
              />
            </div>
          </div>

          <div className="terms-container">
            <div className={`terms-text ${showFullTerms ? 'expanded' : ''}`}>
              <h3>Terms of Access & User Consent</h3>
              <p>By accessing and using the MetaTip AR Anatomy Platform (“the Platform”), you agree to the following terms and conditions:</p>
              
              <h4>1. Access Code Usage</h4>
              <p>Access to the Platform may be provided through a unique access code.</p>
              <p>Access codes are personal and non-transferable.</p>
              <p>You agree not to share, distribute, or reuse your access code with any other individual.</p>
              <p>Any misuse, sharing, or unauthorized distribution of access codes may result in revocation of access without notice.</p>
              
              <h4>2. User Information Collection</h4>
              <p>To access the Platform, we collect the following information:</p>
              <ul>
                <li>First name and last name</li>
                <li>Email address</li>
                <li>Phone number</li>
              </ul>
              <p>This information is collected for purposes including, but not limited to: Granting and managing access to the Platform; Personalizing your experience within the Platform; Communicating important updates, educational content, or platform-related information; Improving our services and user experience.</p>
              <p>We do not sell or misuse your personal information. Your data is handled in accordance with applicable data protection standards.</p>

              <h4>3. Consent</h4>
              <p>By submitting your information and continuing to use the Platform, you:</p>
              <ul>
                <li>Confirm that the information provided is accurate</li>
                <li>Consent to the collection and use of your name, email address, and phone number as described above</li>
                <li>Acknowledge and accept these Terms of Access</li>
              </ul>
              
              <h4>4. Educational & Demonstration Use</h4>
              <p>The Platform is intended for educational and demonstrational purposes only.</p>
              <p>The 3D and AR anatomical models do not replace professional medical advice, diagnosis, or treatment.</p>

              <h4>5. Changes & Updates</h4>
              <p>We reserve the right to update or modify these terms at any time. Continued use of the Platform after changes are made constitutes acceptance of the updated terms.</p>
            </div>
            <button 
              type="button" 
              className="toggle-terms" 
              onClick={() => setShowFullTerms(!showFullTerms)}
            >
              {showFullTerms ? 'Show Less' : 'Read Full Terms'}
            </button>
          </div>

          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
            />
            <span className="checkmark"></span>
            <span className="checkbox-text">
              I agree to the Terms of Access and consent to the collection and use of my name, email, and phone number. I understand that access codes are personal and must not be shared.
            </span>
          </label>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Processing...' : 'Complete Registration'}
          </button>
          
          <button type="button" className="cancel-btn" onClick={handleCancel} disabled={loading}>
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
};

export default ConsentForm;
